import { BeckyModel, removeHash } from '@wix/thunderbolt-becky-root'
import type { RefSchemas } from '@wix/thunderbolt-becky-types'
import {
	CatharsisMegaStoreSymbol,
	ComponentsCssStringifierSymbol,
	MegaStoreWithSubscriptions,
} from '@wix/thunderbolt-catharsis'
import { IComponentCssStringifier } from '@wix/thunderbolt-catharsis-extensions'
import { CatharsisConstants, CatharsisSingleTransactionSymbol } from '@wix/thunderbolt-catharsis-ioc'
import { Container } from '@wix/thunderbolt-ioc'
import { commonLoader } from './common'
import { getModuleLoader } from './loaders/css'
import type { ModuleLoaderFactory } from './types'

function getPresets(documentData: any, pageId: string): Array<string> {
	const widgetDescriptors = Object.values(documentData) as Array<{
		type: string
		rootCompId: string
		presets: Array<string>
	}>
	const widgetDescriptor = widgetDescriptors.find(
		(dataItem) => dataItem.type === 'WidgetDescriptor' && dataItem.rootCompId === '#' + pageId
	)
	const presets = widgetDescriptor!.presets.map((presetRef) => {
		const { presetId } = documentData[presetRef.slice(1)]
		const id = removeHash(presetId)!
		return id
	})
	return presets
}

const getCatharsisConstants = (beckyModel: BeckyModel, isEditor: boolean, schemas: RefSchemas): CatharsisConstants => {
	return {
		isExperimentOpen: (exp) => !!beckyModel.experiments[exp],
		isResponsiveMasterPage: !isEditor && beckyModel.isResponsive && beckyModel.pageCompId === 'masterPage',
		isEditor,
		schemas,
		experiments: beckyModel.experiments,
		componentsMappers: beckyModel.componentsMappers,
	}
}

const fontsCollector = (megaStore: MegaStoreWithSubscriptions) => {
	const fontsCss: Set<string> = new Set()
	megaStore.getChildStore('fontFacesCss').subscribe((__, fontFacesCss: Array<string>) => {
		fontFacesCss.forEach((fontFaceCss) => fontFacesCss && fontsCss.add(fontFaceCss))
	})
	return fontsCss
}

// This method is used for public flow and for testing the editor flow
export const getCatharsisCssInternal = (loaderFactory: ModuleLoaderFactory, isEditor: boolean) => (
	beckyModel: BeckyModel,
	schemas: RefSchemas,
	runAndReport: <T>(fn: () => T, name: string) => T
) => {
	// Create the container and load the modules
	const container = Container()
	const catharsisConstants = getCatharsisConstants(beckyModel, isEditor, schemas)
	const featuresLoader = loaderFactory(catharsisConstants)
	container.load(featuresLoader, commonLoader)

	// Get the megaStore and singleTransaction from the container
	const megaStore = container.get(CatharsisMegaStoreSymbol)
	const singleTransaction = container.get(CatharsisSingleTransactionSymbol)
	const {
		getComponentsCssData,
		stringifyComponentsCssData,
		generatePresetsCss,
	}: IComponentCssStringifier = container.get(ComponentsCssStringifierSymbol)

	const fontsCss = fontsCollector(megaStore as MegaStoreWithSubscriptions)

	// Execute the singleTransaction
	runAndReport(() => singleTransaction.execute(beckyModel, runAndReport), `singleTransaction`)

	// resolve all components css
	const { viewMode, pageCompId, shouldSplitByPresets } = beckyModel
	const allCompsCss = runAndReport(() => {
		const css = runAndReport(() => getComponentsCssData(pageCompId, viewMode === 'mobile'), 'getComponentsCssData')

		if (shouldSplitByPresets) {
			const presets = getPresets(beckyModel.data.document_data, pageCompId)
			const presetsToComponentsCss = runAndReport(() => generatePresetsCss(css, presets), 'generatePresetsCss')
			return { css, cssResult: presetsToComponentsCss }
		}

		const cssString = runAndReport(() => stringifyComponentsCssData(css), 'stringifyComponentsCssData')
		return { css, cssResult: cssString }
	}, 'stringifyAllComponentsCss')

	const cssResult = shouldSplitByPresets
		? allCompsCss.cssResult
		: runAndReport(
				() =>
					[
						/* catharsis font faces */
						...fontsCss,
						/* catharsis components css */
						allCompsCss.cssResult,
					].join('\n'),
				'concat all css'
		  )

	return { megaStore, fontsCss, css: allCompsCss.css, cssResult }
}

export const getViewerCatharsisCss = getCatharsisCssInternal(getModuleLoader, false)
